// Feedback button (krug 3, U5): a short message and an optional screenshot. The picture is
// scaled down in the browser (longest side 1280 px, JPEG) before it is sent.

import { useId, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router';
import { FEEDBACK_RULES } from '@lastday/shared';
import { useSendFeedback } from '@/api/hooks';
import { t } from '@/i18n';
import { cx } from '@/lib/format';
import { PathIcon, P } from './Icon';
import { Sheet } from './Sheet';
import { Button, FormError, Spinner } from './ui';

async function shrink(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('bad image'));
      i.src = url;
    });
    const scale = Math.min(1, FEEDBACK_RULES.screenshotMaxSide / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
    for (const q of [0.82, 0.65, 0.5]) {
      const data = canvas.toDataURL('image/jpeg', q);
      if (data.length <= FEEDBACK_RULES.maxScreenshotChars) return data;
    }
    throw new Error('too big');
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function FeedbackSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { pathname } = useLocation();
  const send = useSendFeedback();
  const textId = useId();
  const fileId = useId();
  const [text, setText] = useState('');
  const [shot, setShot] = useState<string | null>(null);
  const [shotError, setShotError] = useState(false);
  const [reading, setReading] = useState(false);

  const close = () => {
    onClose();
    if (send.isSuccess) {
      setText('');
      setShot(null);
      send.reset();
    }
  };
  const pick = async (f: File | undefined) => {
    setShotError(false);
    if (!f) return setShot(null);
    setReading(true);
    try {
      setShot(await shrink(f));
    } catch {
      setShot(null);
      setShotError(true);
    } finally {
      setReading(false);
    }
  };
  const ok = text.trim().length >= FEEDBACK_RULES.minText;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!ok || send.isPending) return;
    send.mutate({ text: text.trim(), page: pathname, screenshot: shot });
  };

  return (
    <Sheet open={open} onClose={close} title={t('common.feedback.title')} maxWidth={480}>
      {send.isSuccess ? (
        <div className="flex flex-col items-start gap-3 p-4" role="status">
          <span className="display text-[20px] font-bold">{t('common.feedback.thanks')}</span>
          <p className="m-0 text-[15px] leading-[22px] text-muted">{t('common.feedback.thanksBody')}</p>
          <Button variant="secondary" size="sm" onClick={close}>
            {t('common.close')}
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-3 p-4">
          <p className="m-0 text-[14px] leading-5 text-muted">{t('common.feedback.intro')}</p>
          <label htmlFor={textId} className="label">
            {t('common.feedback.label')}
          </label>
          <textarea
            id={textId}
            value={text}
            maxLength={FEEDBACK_RULES.maxText}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('common.feedback.placeholder')}
            className="min-h-[120px] w-full resize-y rounded-[6px] border border-line bg-bg px-3 py-2.5 text-[15px] leading-[22px] text-text outline-none placeholder:text-muted focus:border-text"
          />
          <div className="flex flex-wrap items-center gap-2.5">
            <label htmlFor={fileId} className="flex h-10 cursor-pointer items-center gap-2 rounded-[6px] border border-line px-3 text-[14px] hover:border-text">
              <PathIcon d={P.plus} size={16} />
              {shot ? t('common.feedback.replaceShot') : t('common.feedback.addShot')}
            </label>
            <input id={fileId} type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={(e) => pick(e.target.files?.[0])} />
            {reading && <Spinner size={16} />}
            {shot && (
              <span className="flex items-center gap-2">
                <img src={shot} alt={t('common.feedback.shotAlt')} className="h-10 w-auto rounded-[3px] border border-border" />
                <button type="button" onClick={() => setShot(null)} className="text-[13px] text-muted underline underline-offset-4 hover:text-text">
                  {t('common.feedback.removeShot')}
                </button>
              </span>
            )}
            {shotError && <FormError>{t('common.feedback.shotError')}</FormError>}
          </div>
          <span className="mono text-[12px] text-muted">{t('common.feedback.page', { page: pathname })}</span>
          <Button type="submit" size="lg" full disabled={!ok || send.isPending || reading} className={cx(!ok && 'opacity-70')}>
            {send.isPending ? <Spinner size={16} /> : t('common.feedback.send')}
          </Button>
        </form>
      )}
    </Sheet>
  );
}
